import { Injectable } from '@angular/core';
import { StorageError } from '@modules/error/classes';

import { StorageData, ClientUserData, ProfessionalUserData, UserLicenseDetail } from '@shared/models/auth.interface';
import { SessionService } from '@shared/services/auth/session.service';
import { AppService } from './app.service';
import { EncryptionService } from './encryption.service';
import { StorageType, StorageTypeChoice } from '@shared/models/core.interface';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  storageData: StorageData<ClientUserData | ProfessionalUserData> | undefined = undefined;
  sessionJTI: string | undefined;

  constructor(
    private _appService: AppService,
    private sessionService: SessionService,
    private encryptionService: EncryptionService
  ) {
    this.sessionJTI = this.sessionService.getJTIFromCurrentRoute();
    this.initializeData(this.sessionJTI);
  }

  flushStorageData() {
    this.storageData = undefined;
    this.sessionJTI = undefined;
  }

  initializeData(jti: string): boolean {
    this.flushStorageData();
    const entireEncryptedData = this.sessionService.getData(jti);
    if (entireEncryptedData) {
      const decryptedData: string = this.encryptionService.decryptData(entireEncryptedData);
      this.storageData = JSON.parse(decryptedData);
      return true;
    }
    return false;
  }

  getLicenseInfo(keyName: keyof UserLicenseDetail) {
    return this.storageData?.license[keyName];
  }

  getClientInfo(keyName: keyof ClientUserData): string {
    const data = this.storageData?.userData as ClientUserData;
    const value = data[keyName];
    return (value && typeof value !== 'boolean') ? value : '';
  }

  getProfessionalInfo(keyName: keyof ProfessionalUserData) {
    const data = this.storageData?.userData as ProfessionalUserData;
    const value = data[keyName];
    return (value && typeof value !== 'boolean') ? value : '';
  }

  getLoggedInUserType(): "CLIENT" | "PROFESSIONAL" {
    this.sessionJTI = this.sessionService.getJTIFromCurrentRoute()
    const initialized = this.initializeData(this.sessionJTI);
    if (initialized)
      return (this.storageData!.userIsClient) ? "CLIENT" : "PROFESSIONAL";
    else
      throw new StorageError("NO DATA FOR CURRENT SESSION", "No data found against current session ID.");
  }

  getClientData() {
    let data: ClientUserData;
    data = this.storageData?.userData as ClientUserData;
    return data;
  }

  getRefreshToken() {
    return this.storageData?.refreshToken;
  }

  canStoreData(storageType: StorageTypeChoice, data: string): boolean {
    const browserLimitInKB = 5 * 1024;
    const userDataSizeInKB = (data.length) / 1024;
    console.debug("User Data: ", userDataSizeInKB);

    let sufficientLocalStorageSpace = false;
    let sufficientSessionStorageSpace = false;

    const appMaxLocalStorageDataLimitInKB = this._appService.getAppSettings().maxLocalStorageSpaceAllowedInKB;
    const userDataSizeWithinAllowedRange = appMaxLocalStorageDataLimitInKB > userDataSizeInKB;

    if (!userDataSizeWithinAllowedRange)
      throw new StorageError('USER DATA EXCEEDS ALLOWED LIMIT', `The application requires the data to be smaller than ${appMaxLocalStorageDataLimitInKB}KB`);

    // For Local Storage //
    if (storageType === "LOCAL" || storageType === "BOTH") {
      const occupiedSpaceInKB = this.getStorageSpaceInKB("LOCAL");
      const remainingSpaceInKB = (browserLimitInKB - occupiedSpaceInKB);
      sufficientLocalStorageSpace = remainingSpaceInKB > userDataSizeInKB;

      console.debug("Occupied: ", occupiedSpaceInKB);
      console.debug("Remaining: ", remainingSpaceInKB);

      if (!sufficientLocalStorageSpace)
        throw new StorageError('INSUFFICIENT LOCAL STORAGE SPACE', 'User data cannot be saved on browser. Please clear browser cache.'); 

      if (storageType !== 'BOTH') return sufficientLocalStorageSpace;
    }

    // For Session Storage //
    if (storageType === "SESSION" || storageType === "BOTH") {
      const occupiedSpaceInKB = this.getStorageSpaceInKB("SESSION");
      const remainingSpaceInKB = (browserLimitInKB - occupiedSpaceInKB);

      sufficientSessionStorageSpace = remainingSpaceInKB > userDataSizeInKB;

      if (!sufficientSessionStorageSpace)
        throw new StorageError('INSUFFICIENT SESSION STORAGE SPACE', 'User data cannot be saved on browser. Please clear browser cache.'); 

      if (storageType !== 'BOTH') return sufficientSessionStorageSpace;
    }

    if (sufficientLocalStorageSpace && sufficientSessionStorageSpace) return true;
    else 
      throw new StorageError('INSUFFICIENT STORAGE SPACE', 'User data cannot be saved on browser. Please clear browser cache.'); 
  }

  getStorageSpaceInKB(storageType: StorageType, UTF16Format: boolean = true): number {
    let totalSize = 0;
    const storage = (storageType === "LOCAL") ? localStorage : sessionStorage;

    for (let key in storage) {
      let mappingSizeInKB = (storage[key].length) / 1024;

      if (!isNaN(mappingSizeInKB) && storage.hasOwnProperty(key)) {
        totalSize += mappingSizeInKB;
      }
    }
    return Math.ceil(totalSize);
  }

}
